'use client';

import * as React from 'react';
import { useLocalParticipant } from '@livekit/components-react';
import { isLocalTrack, type LocalVideoTrack } from 'livekit-client';
import {
  NONE_BACKGROUND_ID,
  findBackgroundOption,
  getBackgroundOptions,
  isCustomBackgroundId,
  subscribeToBackgroundOptions,
} from './backgrounds';
import type { BackgroundOption } from './backgrounds';
import {
  addCustomBackground,
  customBackgroundsReady,
  removeCustomBackground,
} from './custom-backgrounds';
import type { AddCustomBackgroundResult } from './custom-backgrounds';

type ProcessorModule = typeof import('@livekit/track-processors');
export type BackgroundProcessorInstance = ReturnType<ProcessorModule['BackgroundProcessor']>;

const STORAGE_KEY = 'octor-camera-background';
/** Default name given by @livekit/track-processors to a BackgroundProcessor. */
const PROCESSOR_NAME = 'background-processor';
/** Mirrors written by scripts/copy-mediapipe-assets.mjs. */
const LOCAL_WASM_PATH = '/mediapipe/wasm';
const LOCAL_MODEL_PATH = '/mediapipe/selfie_segmenter.tflite';
/**
 * Must equal the @mediapipe/tasks-vision version @livekit/track-processors ships:
 * the JS loader and the wasm come in pairs. scripts/copy-mediapipe-assets.mjs
 * fails the build when they drift apart.
 */
export const TASKS_VISION_VERSION = '0.10.14';
const CDN_WASM_PATH = `https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@${TASKS_VISION_VERSION}/wasm`;
/** A CDN that does not answer this fast is treated as unreachable. */
const CDN_PROBE_TIMEOUT_MS = 1500;

export type BackgroundStatus = 'idle' | 'pending' | 'unsupported' | 'error';

type BackgroundState = { id: string; status: BackgroundStatus };

const INITIAL_STATE: BackgroundState = { id: NONE_BACKGROUND_ID, status: 'idle' };

// Module-level store: the control-bar picker, the pre-join picker, the settings
// menu and the code that talks to the camera track share one selection.
let state: BackgroundState = INITIAL_STATE;
let hydratePromise: Promise<void> | undefined;
const listeners = new Set<() => void>();

function setState(next: Partial<BackgroundState>) {
  const merged = { ...state, ...next };
  if (merged.id === state.id && merged.status === state.status) {
    return;
  }
  state = merged;
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot() {
  return state;
}

function getServerSnapshot() {
  return INITIAL_STATE;
}

// Bumped on every explicit pick, so a restore that is still waiting for
// IndexedDB never overrides a choice the user made meanwhile.
let selectionEpoch = 0;

/** Monotonic counter of explicit picks: async flows compare it around their
 * awaits so they do not overwrite a choice made in the meantime. */
export function getSelectionEpoch(): number {
  return selectionEpoch;
}

/** Synchronous read of the current pick, for the same race guards. */
export function getSelectedBackgroundId(): string {
  return state.id;
}

/**
 * Restores the last chosen background once, after hydration. A custom id waits
 * for its record to load from IndexedDB instead of falling straight back to
 * "none"; if the record was deleted earlier, findBackgroundOption performs the
 * usual fallback.
 */
function hydrate(): Promise<void> {
  if (typeof window === 'undefined') {
    return Promise.resolve();
  }
  hydratePromise ??= restoreStoredSelection();
  return hydratePromise;
}

async function restoreStoredSelection() {
  let stored: string | null;
  try {
    stored = window.localStorage.getItem(STORAGE_KEY);
  } catch {
    // Storage can be unavailable (private mode, blocked cookies) — ignore.
    return;
  }
  if (!stored || stored === state.id) {
    return;
  }
  if (isCustomBackgroundId(stored)) {
    const epoch = selectionEpoch;
    await customBackgroundsReady();
    if (selectionEpoch !== epoch) {
      return;
    }
  }
  if (stored === state.id) {
    return;
  }
  setState({ id: findBackgroundOption(stored).id });
}

export function selectBackground(id: string) {
  selectionEpoch += 1;
  const option = findBackgroundOption(id);
  setState({ id: option.id, status: state.status === 'unsupported' ? 'unsupported' : 'idle' });
  try {
    window.localStorage.setItem(STORAGE_KEY, option.id);
  } catch {
    // Not being able to remember the choice must not break switching it.
  }
}

// --- Segmentation assets ---------------------------------------------------

let modulePromise: Promise<ProcessorModule> | undefined;

function loadModule() {
  // Loaded on demand: the segmentation code is heavy and most sessions never
  // leave the default background.
  modulePromise ??= import('@livekit/track-processors');
  return modulePromise;
}

type AssetPaths = { tasksVisionFileSet?: string; modelAssetPath?: string };

let assetPathsPromise: Promise<AssetPaths> | undefined;

async function isServed(url: string, timeoutMs?: number) {
  const controller = new AbortController();
  const timer = timeoutMs ? setTimeout(() => controller.abort(), timeoutMs) : undefined;
  try {
    const response = await fetch(url, { method: 'HEAD', signal: controller.signal });
    return response.ok;
  } catch {
    return false;
  } finally {
    if (timer) clearTimeout(timer);
  }
}

/**
 * The wasm bundle (~3 MB gzipped) comes from jsdelivr when it answers quickly,
 * otherwise from our own origin. Employees reach octor over the corporate VPN
 * at ~120 KB/s, so our copy took ~45 s, while jsdelivr goes straight to the
 * internet in under a second. The small model stays on our origin (Google may
 * be unreachable). Undefined entries make the library use its own defaults.
 */
function loadAssetPaths() {
  assetPathsPromise ??= (async () => {
    const [cdnUp, hasWasm, hasModel] = await Promise.all([
      isServed(`${CDN_WASM_PATH}/vision_wasm_internal.js`, CDN_PROBE_TIMEOUT_MS),
      isServed(`${LOCAL_WASM_PATH}/vision_wasm_internal.js`),
      isServed(LOCAL_MODEL_PATH),
    ]);
    return {
      tasksVisionFileSet: cdnUp ? CDN_WASM_PATH : hasWasm ? LOCAL_WASM_PATH : undefined,
      modelAssetPath: hasModel ? LOCAL_MODEL_PATH : undefined,
    };
  })();
  return assetPathsPromise;
}

function prefetch(url: string) {
  if (document.head.querySelector(`link[rel="prefetch"][href="${url}"]`)) {
    return;
  }
  const link = document.createElement('link');
  link.rel = 'prefetch';
  link.href = url;
  document.head.appendChild(link);
}

/**
 * Pulls the segmentation code and assets into cache while the user is still
 * looking at the picker, so picking a background does not start from zero.
 */
export function prewarmBackgrounds() {
  if (typeof window === 'undefined') {
    return;
  }
  loadModule().catch(() => undefined);
  loadAssetPaths()
    .then(({ tasksVisionFileSet, modelAssetPath }) => {
      if (tasksVisionFileSet) {
        prefetch(`${tasksVisionFileSet}/vision_wasm_internal.wasm`);
      }
      if (modelAssetPath) {
        prefetch(modelAssetPath);
      }
    })
    .catch(() => undefined);
}

/**
 * Starts loading the segmentation assets once the browser is idle after the
 * picker shows up (pre-join screen, call toolbar), so the first pick — blur
 * included — does not wait for the download.
 */
export function usePrewarmBackgroundsOnIdle() {
  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    if ('requestIdleCallback' in window) {
      const id = window.requestIdleCallback(() => prewarmBackgrounds(), { timeout: 3000 });
      return () => window.cancelIdleCallback(id);
    }
    const timer = setTimeout(prewarmBackgrounds, 1500);
    return () => clearTimeout(timer);
  }, []);
}

// --- Applying the background ----------------------------------------------

type SwitchOptions =
  | { mode: 'background-blur'; blurRadius: number }
  | { mode: 'virtual-background'; imagePath: string };

function switchOptions(option: BackgroundOption): SwitchOptions | undefined {
  switch (option.kind) {
    case 'blur':
      return { mode: 'background-blur', blurRadius: option.blurRadius };
    case 'image':
      return { mode: 'virtual-background', imagePath: option.src };
    default:
      return undefined;
  }
}

async function createProcessor(options: SwitchOptions) {
  const [module, assetPaths] = await Promise.all([loadModule(), loadAssetPaths()]);
  if (!module.supportsBackgroundProcessors()) {
    setState({ status: 'unsupported' });
    return undefined;
  }
  return module.BackgroundProcessor({
    ...options,
    assetPaths,
    // The first processed frame is the moment the background actually shows up.
    onFrameProcessed: () => setState({ status: 'idle' }),
  });
}

function ourProcessor(track: LocalVideoTrack) {
  const processor = track.getProcessor();
  return processor?.name === PROCESSOR_NAME
    ? (processor as BackgroundProcessorInstance)
    : undefined;
}

/** Creates the processor to publish the camera with, for the current selection. */
export async function createPublishProcessor() {
  await hydrate();
  const options = switchOptions(findBackgroundOption(getSnapshot().id));
  if (!options) {
    return undefined;
  }
  setState({ status: 'pending' });
  try {
    return await createProcessor(options);
  } catch (error) {
    console.error('Failed to prepare camera background', error);
    setState({ status: 'error' });
    return undefined;
  }
}

/** Which background is live on a given track, so remounts don't re-apply it. */
const appliedByTrack = new WeakMap<LocalVideoTrack, string>();
// Processor swaps must not overlap, otherwise the camera track ends up with the
// result of whichever call happened to finish last.
let queue: Promise<void> = Promise.resolve();

async function applyBackground(track: LocalVideoTrack, id: string) {
  if (appliedByTrack.get(track) === id) {
    return;
  }
  const option = findBackgroundOption(id);
  const options = switchOptions(option);
  const running = ourProcessor(track);

  if (!options) {
    // Keep the processor attached but idle: it stops touching frames, and turning
    // a background back on skips loading the model again.
    if (running) {
      await running.switchTo({ mode: 'disabled' });
    } else if (track.getProcessor()) {
      await track.stopProcessor();
    }
    appliedByTrack.set(track, option.id);
    setState({ status: 'idle' });
    return;
  }

  setState({ status: 'pending' });
  try {
    if (running) {
      // Switching modes in place avoids tearing the published track down and back
      // up on every click.
      await running.switchTo(options);
    } else {
      const processor = await createProcessor(options);
      if (!processor) {
        return;
      }
      await track.setProcessor(processor);
    }
    appliedByTrack.set(track, option.id);
    setState({ status: 'idle' });
  } catch (error) {
    console.error('Failed to apply camera background', error);
    setState({ status: 'error' });
  }
}

function enqueue(track: LocalVideoTrack, id: string) {
  queue = queue.then(() => applyBackground(track, id)).catch(() => undefined);
}

// --- Hooks -----------------------------------------------------------------

export type BackgroundSelection = {
  options: BackgroundOption[];
  activeId: string;
  activeOption: BackgroundOption;
  status: BackgroundStatus;
  select: (id: string) => void;
  addCustom: (file: File) => Promise<AddCustomBackgroundResult>;
  removeCustom: (id: string) => Promise<boolean>;
};

/** Uploaded backgrounds appear in the list asynchronously; this tracks that list. */
function useBackgroundOptions(): BackgroundOption[] {
  return React.useSyncExternalStore(
    subscribeToBackgroundOptions,
    getBackgroundOptions,
    getBackgroundOptions,
  );
}

function useBackgroundState() {
  const value = React.useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  React.useEffect(() => {
    void hydrate();
    // Uploaded backgrounds must show up in the picker even when the stored id
    // is not one of them, so start the load on every surface.
    void customBackgroundsReady();
  }, []);
  return value;
}

/** The picked background, without needing a room — also used on the pre-join page. */
export function useBackgroundSelection(): BackgroundSelection {
  const { id, status } = useBackgroundState();
  const options = useBackgroundOptions();
  return {
    options,
    activeId: id,
    activeOption: findBackgroundOption(id),
    status,
    select: selectBackground,
    addCustom: addCustomBackground,
    removeCustom: removeCustomBackground,
  };
}

/**
 * Keeps the local camera track in sync with the selected background. Safe to use
 * from several components at once — the work is de-duplicated per track.
 */
export function useCameraBackground(): BackgroundSelection & { hasCamera: boolean } {
  const selection = useBackgroundSelection();
  const { cameraTrack } = useLocalParticipant();
  const track = isLocalTrack(cameraTrack?.track)
    ? (cameraTrack.track as LocalVideoTrack)
    : undefined;
  // Identity changes when uploaded backgrounds finish loading, which re-runs
  // the effect and applies a restored custom background to the track.
  const activeOption = findBackgroundOption(selection.activeId);

  React.useEffect(() => {
    if (!track) {
      return;
    }
    enqueue(track, activeOption.id);
  }, [track, activeOption]);

  return { ...selection, hasCamera: !!track };
}

/**
 * Processor for the pre-join preview. It is created on the first background the
 * user actually picks, so opening the page costs nothing; after that the same
 * instance is reused and only switches modes.
 */
export function usePreviewBackgroundProcessor(): BackgroundProcessorInstance | undefined {
  const backgroundOption = findBackgroundOption(useBackgroundState().id);
  const [processor, setProcessor] = React.useState<BackgroundProcessorInstance>();
  const appliedId = React.useRef<string>();

  React.useEffect(() => {
    let cancelled = false;
    const options = switchOptions(backgroundOption);
    const id = backgroundOption.id;

    const run = async () => {
      if (appliedId.current === id) {
        return;
      }
      try {
        if (!options) {
          appliedId.current = id;
          await processor?.switchTo({ mode: 'disabled' });
          setState({ status: 'idle' });
          return;
        }
        setState({ status: 'pending' });
        if (processor) {
          appliedId.current = id;
          await processor.switchTo(options);
          setState({ status: 'idle' });
          return;
        }
        const created = await createProcessor(options);
        if (created && !cancelled) {
          appliedId.current = id;
          setProcessor(created);
        }
      } catch (error) {
        console.error('Failed to apply camera background', error);
        setState({ status: 'error' });
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [backgroundOption, processor]);

  return processor;
}
